/**
 * Inicio del Creador de reportes: biblioteca de los reportes construidos con el motor,
 * agrupados Cliente → Proyecto → Reporte, con búsqueda, filtros, recientes y las acciones
 * que permite el rol (abrir, editar, compartir, duplicar, eliminar). Las reglas vienen en
 * `capabilities` desde /api/creator/library (respaldo en reportCapabilities.js).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ink, studio, SearchField, FilterDock, FilterPill } from '../studioUi';
import { capabilitiesFor } from './reportCapabilities';

const RECENT_COUNT = 6;
const STATUS_OPTIONS = [
    { value: 'published', label: 'Publicados' },
    { value: 'draft', label: 'Borradores' },
    { value: 'mine', label: 'Creados por mí' },
];

/** «hace 5 min», «hace 3 h», «hace 2 d» o la fecha corta. */
export function relativeTime(iso, now = Date.now()) {
    const time = Date.parse(iso || '');
    if (!Number.isFinite(time)) return '';
    const minutes = Math.max(0, Math.round((now - time) / 60000));
    if (minutes < 1) return 'hace un momento';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.round(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.round(hours / 24);
    if (days < 7) return `hace ${days} d`;
    return new Date(time).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
}

function editedLabel(report) {
    const when = relativeTime(report.updatedAt);
    if (!when) return '';
    const who = report.updatedByName || report.createdByName;
    return who ? `Editado ${when} por ${who}` : `Editado ${when}`;
}

function StatusChip({ draft }) {
    return (
        <span style={{ ...styles.chip, ...(draft ? styles.chipDraft : styles.chipLive) }}>
            {draft ? 'Borrador' : 'Publicado'}
        </span>
    );
}

/** Menú «⋯» con las acciones secundarias de un reporte. */
function MoreMenu({ items }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, [open]);
    const visible = items.filter(item => item.show);
    if (!visible.length) return null;
    return (
        <div ref={ref} style={{ position: 'relative' }}>
            <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                title="Más acciones"
                onClick={() => setOpen(value => !value)}
                style={styles.iconBtn}
            >
                ⋯
            </button>
            {open && (
                <div role="menu" style={styles.menu}>
                    {visible.map(item => (
                        <button
                            key={item.label}
                            type="button"
                            role="menuitem"
                            onClick={() => { setOpen(false); item.onClick(); }}
                            style={{ ...styles.menuItem, ...(item.danger ? { color: ink.danger } : {}) }}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/** Acciones de un reporte: la principal visible y el resto en «⋯». */
function ReportActions({ report, caps, handlers }) {
    return (
        <div style={styles.actions} onClick={(e) => e.stopPropagation()}>
            {caps.canOpen && (
                <button type="button" style={styles.textBtn} onClick={() => handlers.onOpen(report)}>Abrir</button>
            )}
            {caps.canEdit && (
                <button type="button" style={styles.textBtn} onClick={() => handlers.onEdit(report)}>Editar</button>
            )}
            {!caps.canEdit && report.isDraft && (
                <button type="button" style={styles.textBtn} onClick={() => handlers.onEdit(report)}>Ver</button>
            )}
            <button type="button" style={styles.textBtn} onClick={() => handlers.onShare(report)}>Compartir</button>
            <MoreMenu
                items={[
                    { label: 'Duplicar', show: caps.canDuplicate, onClick: () => handlers.onDuplicate(report) },
                    { label: 'Ver en solo lectura', show: !caps.canEdit && !report.isDraft, onClick: () => handlers.onEdit(report) },
                    { label: 'Eliminar', show: caps.canDelete, danger: true, onClick: () => handlers.onDelete(report) },
                ]}
            />
        </div>
    );
}

function RecentCard({ report, caps, handlers }) {
    const primary = caps.canEdit || report.isDraft ? handlers.onEdit : handlers.onOpen;
    return (
        <div style={styles.recentCard}>
            <button type="button" onClick={() => primary(report)} style={styles.recentHit}>
                <span style={styles.recentTop}>
                    <StatusChip draft={report.isDraft} />
                    {report.pageCount > 1 && <span style={styles.meta}>{report.pageCount} hojas</span>}
                </span>
                <strong style={styles.recentName}>{report.reporteNombre}</strong>
                <span style={styles.meta}>{[report.cliente, report.proyecto].filter(Boolean).join(' · ')}</span>
                <span style={styles.meta}>{editedLabel(report)}</span>
            </button>
            <ReportActions report={report} caps={caps} handlers={handlers} />
        </div>
    );
}

function ReportRow({ report, caps, handlers }) {
    const primary = caps.canEdit || report.isDraft ? handlers.onEdit : handlers.onOpen;
    return (
        <div style={styles.row}>
            <button type="button" onClick={() => primary(report)} style={styles.rowHit}>
                <span style={styles.rowIcon} aria-hidden="true">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={ink.accent} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <path d="M8 16v-4M12 16V8M16 16v-6" />
                    </svg>
                </span>
                <span style={styles.rowMain}>
                    <span style={styles.rowTitleLine}>
                        <strong style={styles.rowName}>{report.reporteNombre}</strong>
                        <StatusChip draft={report.isDraft} />
                    </span>
                    <span style={styles.meta}>
                        {[
                            report.pageCount ? `${report.pageCount} hoja${report.pageCount === 1 ? '' : 's'}` : '',
                            editedLabel(report),
                            report.accessType ? `Tu acceso: ${report.accessType === 'edit' ? 'Edición' : 'Vista'}` : (report.isAuthor ? 'Creado por ti' : ''),
                        ].filter(Boolean).join(' · ')}
                    </span>
                </span>
            </button>
            <ReportActions report={report} caps={caps} handlers={handlers} />
        </div>
    );
}

const CreatorHome = ({
    reports = [],
    loading,
    error,
    onRetry,
    clients = [],
    canCreate,
    perms,
    uid,
    onNew,
    onOpen,
    onEdit,
    onShare,
    onDuplicate,
    onDelete,
}) => {
    const [query, setQuery] = useState('');
    const [status, setStatus] = useState('');
    const [clientId, setClientId] = useState('');
    const [sort, setSort] = useState('recent');
    const [collapsed, setCollapsed] = useState(() => new Set());

    const handlers = { onOpen, onEdit, onShare, onDuplicate, onDelete };
    const capsOf = (report) => capabilitiesFor(report, perms, uid);

    const clientName = (report) => clients.find(item => item.id === report.clientId)?.nombre || report.cliente || 'Sin cliente';

    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        return reports.filter((report) => {
            if (clientId && report.clientId !== clientId) return false;
            if (status === 'published' && report.isDraft) return false;
            if (status === 'draft' && !report.isDraft) return false;
            if (status === 'mine' && !report.isAuthor) return false;
            if (!needle) return true;
            return `${report.reporteNombre} ${report.cliente} ${report.proyecto}`.toLowerCase().includes(needle);
        });
    }, [reports, query, status, clientId]);

    const compare = useMemo(() => (sort === 'name'
        ? (a, b) => a.reporteNombre.localeCompare(b.reporteNombre, 'es')
        : (a, b) => (Date.parse(b.updatedAt || '') || 0) - (Date.parse(a.updatedAt || '') || 0)
    ), [sort]);

    const recent = useMemo(() => (
        [...reports]
            .filter(report => report.updatedAt)
            .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))
            .slice(0, RECENT_COUNT)
    ), [reports]);

    // Cliente → Proyecto → reportes, clientes y proyectos en orden alfabético.
    const tree = useMemo(() => {
        const byClient = new Map();
        filtered.forEach((report) => {
            const cKey = report.clientId || `name:${report.cliente}`;
            if (!byClient.has(cKey)) byClient.set(cKey, { key: cKey, name: clientName(report), projects: new Map(), count: 0 });
            const client = byClient.get(cKey);
            const pKey = report.projectId || `name:${report.proyecto}`;
            if (!client.projects.has(pKey)) client.projects.set(pKey, { key: pKey, name: report.proyecto || 'Sin proyecto', reports: [] });
            client.projects.get(pKey).reports.push(report);
            client.count += 1;
        });
        return Array.from(byClient.values())
            .sort((a, b) => a.name.localeCompare(b.name, 'es'))
            .map(client => ({
                ...client,
                projects: Array.from(client.projects.values())
                    .sort((a, b) => a.name.localeCompare(b.name, 'es'))
                    .map(project => ({ ...project, reports: [...project.reports].sort(compare) })),
            }));
    }, [filtered, compare]); // eslint-disable-line react-hooks/exhaustive-deps

    const clientOptions = useMemo(() => {
        const seen = new Map();
        reports.forEach(report => { if (report.clientId) seen.set(report.clientId, clientName(report)); });
        return Array.from(seen.entries()).sort((a, b) => a[1].localeCompare(b[1], 'es'));
    }, [reports]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggle = (key) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    const filtering = Boolean(query.trim() || status || clientId);
    const drafts = reports.filter(report => report.isDraft).length;

    return (
        <div style={styles.scroll}>
        <div style={styles.page}>
            <div style={studio.header}>
                <div>
                    <p style={studio.kicker}>Creador de reportes</p>
                    <h2 style={studio.title}>Reportes del motor</h2>
                    <p style={studio.subtitle}>
                        Abre, edita o comparte los dashboards construidos con el Creador, organizados por cliente y proyecto.
                    </p>
                </div>
                <div style={styles.headerSide}>
                    <div style={studio.stats}>
                        <div style={studio.stat}>
                            <span style={studio.statValue}>{reports.length - drafts}</span>
                            <span style={studio.statLabel}>Publicados</span>
                        </div>
                        <div style={studio.stat}>
                            <span style={studio.statValue}>{drafts}</span>
                            <span style={studio.statLabel}>Borradores</span>
                        </div>
                    </div>
                    {canCreate && (
                        <button
                            type="button"
                            style={studio.primaryBtn}
                            onClick={() => onNew({ clientId: clientId || '' })}
                        >
                            + Nuevo reporte
                        </button>
                    )}
                </div>
            </div>

            <FilterDock>
                <div style={styles.filters}>
                    <SearchField
                        value={query}
                        onChange={setQuery}
                        placeholder="Buscar por reporte, cliente o proyecto…"
                        style={{ marginBottom: 0, flex: '1 1 260px' }}
                    />
                    <div style={studio.filterPills}>
                        <FilterPill value={clientId} onChange={setClientId} emptyLabel="Todos los clientes">
                            {clientOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
                        </FilterPill>
                        <FilterPill value={status} onChange={setStatus} emptyLabel="Todos los estados">
                            {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                        </FilterPill>
                        <select value={sort} onChange={(e) => setSort(e.target.value)} style={styles.sortSelect} title="Orden">
                            <option value="recent">Modificados recientemente</option>
                            <option value="name">Nombre (A–Z)</option>
                        </select>
                    </div>
                </div>
            </FilterDock>

            {error && (
                <div style={styles.error}>
                    {error}
                    {onRetry && <button type="button" style={styles.textBtn} onClick={onRetry}>Reintentar</button>}
                </div>
            )}

            {loading && !reports.length ? (
                <div style={styles.skeletonWrap} aria-busy="true">
                    {[0, 1, 2, 3].map(i => <div key={i} style={styles.skeleton} />)}
                </div>
            ) : (
                <>
                    {!filtering && recent.length > 0 && (
                        <section>
                            <h3 style={styles.sectionTitle}>Recientes</h3>
                            <div style={styles.recentGrid}>
                                {recent.map(report => (
                                    <RecentCard key={report.id} report={report} caps={capsOf(report)} handlers={handlers} />
                                ))}
                            </div>
                        </section>
                    )}

                    <section>
                        <h3 style={styles.sectionTitle}>
                            {filtering ? `${filtered.length} resultado${filtered.length === 1 ? '' : 's'}` : 'Todos los reportes'}
                        </h3>
                        {tree.length === 0 && (
                            <div style={styles.empty}>
                                {reports.length === 0
                                    ? (canCreate
                                        ? 'Aún no hay reportes del motor que puedas ver. Crea el primero con «Nuevo reporte».'
                                        : 'Aún no tienes reportes del motor compartidos contigo.')
                                    : 'Ningún reporte coincide con los filtros.'}
                            </div>
                        )}
                        <div style={styles.tree}>
                            {tree.map(client => {
                                const closed = collapsed.has(client.key) && !filtering;
                                return (
                                    <div key={client.key} style={studio.card}>
                                        <button type="button" onClick={() => toggle(client.key)} style={styles.clientHead} aria-expanded={!closed}>
                                            <span style={{ ...styles.chevron, transform: closed ? 'rotate(-90deg)' : 'none' }}>▾</span>
                                            <span style={styles.clientName}>{client.name}</span>
                                            <span style={studio.tabBadge}>{client.count}</span>
                                        </button>
                                        {!closed && client.projects.map(project => (
                                            <div key={project.key} style={styles.project}>
                                                <div style={styles.projectHead}>
                                                    <span style={styles.projectName}>{project.name}</span>
                                                    <span style={styles.meta}>{project.reports.length}</span>
                                                </div>
                                                {project.reports.map(report => (
                                                    <ReportRow key={report.id} report={report} caps={capsOf(report)} handlers={handlers} />
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </>
            )}
        </div>
        </div>
    );
};

const styles = {
    // El contenedor del Creador corta el desborde (el lienzo maneja su propio scroll).
    scroll: { flex: 1, minHeight: 0, overflowY: 'auto' },
    page: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        maxWidth: 1180,
        width: '100%',
        margin: '0 auto',
        padding: '18px 8px 40px',
        boxSizing: 'border-box',
    },
    headerSide: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' },
    filters: { display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' },
    sortSelect: { ...studio.filterPill, backgroundImage: 'none', paddingRight: 14 },
    sectionTitle: {
        margin: '0 0 10px',
        fontSize: '0.78rem',
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: ink.faint,
    },
    recentGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 12 },
    recentCard: {
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 14,
        overflow: 'visible',
    },
    recentHit: {
        appearance: 'none',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: 4,
        padding: '14px 16px 8px',
        backgroundColor: 'transparent',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
        minWidth: 0,
    },
    recentTop: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 },
    recentName: {
        fontSize: '1rem',
        fontWeight: 600,
        color: ink.text,
        letterSpacing: '-0.01em',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    tree: { display: 'flex', flexDirection: 'column', gap: 12 },
    clientHead: {
        appearance: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'inherit',
        textAlign: 'left',
    },
    chevron: { color: ink.faint, fontSize: 14, transition: 'transform 0.15s ease', display: 'inline-block', width: 14 },
    clientName: { fontSize: '1.05rem', fontWeight: 600, color: ink.text, letterSpacing: '-0.01em', flex: 1 },
    project: { marginTop: 14, paddingLeft: 24 },
    projectHead: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        paddingBottom: 6,
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.line,
    },
    projectName: { fontSize: '0.86rem', fontWeight: 600, color: ink.muted },
    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        padding: '6px 0',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: ink.wash,
    },
    rowHit: {
        appearance: 'none',
        flex: '1 1 320px',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '6px 4px',
        backgroundColor: 'transparent',
        border: 'none',
        textAlign: 'left',
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    rowIcon: {
        width: 34,
        height: 34,
        borderRadius: 10,
        backgroundColor: ink.accentSoft,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    rowMain: { display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 },
    rowTitleLine: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
    rowName: {
        fontSize: '0.95rem',
        fontWeight: 600,
        color: ink.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    meta: { fontSize: '0.8rem', color: ink.faint },
    chip: {
        fontSize: '0.7rem',
        fontWeight: 700,
        borderRadius: 999,
        padding: '2px 8px',
        whiteSpace: 'nowrap',
        letterSpacing: '0.02em',
    },
    chipDraft: { backgroundColor: '#fef7e0', color: '#8a5a00' },
    chipLive: { backgroundColor: '#e6f4ea', color: '#137333' },
    actions: { display: 'flex', alignItems: 'center', gap: 2, padding: '0 8px 8px', flexWrap: 'wrap' },
    textBtn: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        color: ink.accentText,
        fontWeight: 600,
        fontSize: '0.84rem',
        padding: '6px 8px',
        borderRadius: 8,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    iconBtn: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        color: ink.muted,
        fontSize: 18,
        lineHeight: 1,
        padding: '4px 10px 8px',
        borderRadius: 8,
        cursor: 'pointer',
    },
    menu: {
        position: 'absolute',
        right: 0,
        top: '100%',
        zIndex: 30,
        minWidth: 180,
        backgroundColor: ink.surface,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: ink.line,
        borderRadius: 12,
        boxShadow: '0 10px 28px rgba(28, 29, 33, 0.14)',
        padding: 6,
        display: 'flex',
        flexDirection: 'column',
    },
    menuItem: {
        appearance: 'none',
        backgroundColor: 'transparent',
        border: 'none',
        textAlign: 'left',
        padding: '9px 12px',
        borderRadius: 8,
        fontSize: '0.88rem',
        color: ink.text,
        cursor: 'pointer',
        fontFamily: 'inherit',
    },
    empty: {
        padding: '28px 20px',
        textAlign: 'center',
        color: ink.muted,
        backgroundColor: ink.wash,
        borderRadius: 14,
        fontSize: '0.92rem',
    },
    error: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '10px 14px',
        borderRadius: 12,
        backgroundColor: ink.dangerSoft,
        color: ink.danger,
        fontSize: '0.9rem',
    },
    skeletonWrap: { display: 'flex', flexDirection: 'column', gap: 12 },
    skeleton: { height: 72, borderRadius: 14, backgroundColor: ink.wash },
};

export default CreatorHome;
